"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  Plus,
  User,
  Film,
  Clock,
  Sparkles,
  Eye,
  Settings,
  X,
  Zap,
  Palette,
  Video,
} from "lucide-react";

// Sample data for the demo
const sampleCharacters = [
  {
    id: 1,
    name: "Alex Chen",
    role: "Protagonist",
    personality: "Determined, Tech-savvy",
    voiceStyle: "Confident, Clear",
    image: "/professional-business-person.png",
  },
  {
    id: 2,
    name: "Dr. Sarah Kim",
    role: "Mentor",
    personality: "Wise, Supportive",
    voiceStyle: "Warm, Authoritative",
    image: "/placeholder-nv144.png",
  },
  {
    id: 3,
    name: "Marcus Rivera",
    role: "Antagonist",
    personality: "Cunning, Ambitious",
    voiceStyle: "Smooth, Persuasive",
    image: "/placeholder-e8n7o.png",
  },
];

const sampleScenes = [
  {
    id: 1,
    title: "Opening Pitch",
    description:
      "Alex presents the revolutionary AI solution to potential investors in a sleek boardroom.",
    characters: ["Alex Chen", "Dr. Sarah Kim"],
    duration: "2:30",
    thumbnail: "/placeholder-jo66q.png",
  },
  {
    id: 2,
    title: "The Challenge",
    description:
      "Marcus reveals the competitive threat that could derail the entire project.",
    characters: ["Marcus Rivera", "Alex Chen"],
    duration: "1:45",
    thumbnail: "/tense-business-meeting-confrontation.jpg",
  },
  {
    id: 3,
    title: "Innovation Breakthrough",
    description:
      "The team discovers a breakthrough that changes everything in their high-tech lab.",
    characters: ["Alex Chen", "Dr. Sarah Kim"],
    duration: "3:15",
    thumbnail: "/placeholder-bp1jc.png",
  },
];

// Character Card Component
const CharacterCard = ({
  character,
  onClick,
}: {
  character: any;
  onClick: any;
}) => (
  <motion.div
    whileHover={{ scale: 1.02, y: -4 }}
    whileTap={{ scale: 0.98 }}
    className="bg-card border border-border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-primary/10"
    onClick={() => onClick(character)}
  >
    <div className="flex flex-col items-center space-y-3">
      <div className="w-16 h-16 rounded-full overflow-hidden bg-muted">
        <img
          src={character.image || "/placeholder.svg"}
          alt={character.name}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="text-center">
        <h3 className="font-semibold text-card-foreground">{character.name}</h3>
        <p className="text-sm text-muted-foreground">{character.role}</p>
        <div className="mt-2 space-y-1">
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">Personality:</span>{" "}
            {character.personality}
          </p>
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">Voice:</span> {character.voiceStyle}
          </p>
        </div>
      </div>
    </div>
  </motion.div>
);

// Scene Card Component
const SceneCard = ({ scene, onClick }: { scene: any; onClick: any }) => (
  <motion.div
    whileHover={{ scale: 1.01, y: -2 }}
    whileTap={{ scale: 0.99 }}
    className="bg-card border border-border rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-primary/10"
    onClick={() => onClick(scene)}
  >
    <div className="aspect-video bg-muted relative overflow-hidden">
      <img
        src={scene.thumbnail || "/placeholder.svg"}
        alt={scene.title}
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
        <Play className="w-8 h-8 text-white" />
      </div>
    </div>
    <div className="p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-card-foreground">{scene.title}</h3>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {scene.duration}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
        {scene.description}
      </p>
      <div className="flex flex-wrap gap-1 mb-3">
        {scene.characters.map((character: any, index: any) => (
          <span
            key={index}
            className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
          >
            {character}
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <button className="flex-1 bg-primary text-primary-foreground px-3 py-1.5 rounded text-xs font-medium hover:bg-primary/90 transition-colors">
          Generate with Veo3
        </button>
        <button className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded text-xs font-medium hover:bg-secondary/90 transition-colors">
          <Sparkles className="w-3 h-3" />
        </button>
      </div>
    </div>
  </motion.div>
);

// Timeline Component
const Timeline = ({ scenes }: { scenes: any }) => (
  <div className="bg-card border border-border rounded-lg p-4">
    <h3 className="font-semibold text-card-foreground mb-4 flex items-center gap-2">
      <Film className="w-4 h-4" />
      Film Timeline
    </h3>
    <div className="flex gap-3 overflow-x-auto pb-2">
      {scenes.map((scene: any, index: any) => (
        <motion.div
          key={scene.id}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="flex-shrink-0 w-32"
        >
          <div className="aspect-video bg-muted rounded overflow-hidden mb-2">
            <img
              src={scene.thumbnail || "/placeholder.svg"}
              alt={scene.title}
              className="w-full h-full object-cover"
            />
          </div>
          <p className="text-xs text-card-foreground font-medium truncate">
            {scene.title}
          </p>
          <p className="text-xs text-muted-foreground">{scene.duration}</p>
        </motion.div>
      ))}
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="flex-shrink-0 w-32 aspect-video bg-muted/50 border-2 border-dashed border-border rounded flex items-center justify-center cursor-pointer hover:bg-muted/70 transition-colors"
      >
        <Plus className="w-6 h-6 text-muted-foreground" />
      </motion.div>
    </div>
  </div>
);

// Tools Sidebar Component
const ToolsSidebar = () => (
  <div className="bg-sidebar border-l border-sidebar-border p-4 space-y-4">
    <h3 className="font-semibold text-sidebar-foreground flex items-center gap-2">
      <Settings className="w-4 h-4" />
      Film Tools Panel
    </h3>

    <div className="space-y-3">
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-sidebar-foreground">
          AI Generation
        </h4>
        <button className="w-full bg-sidebar-primary text-sidebar-primary-foreground px-3 py-2 rounded text-sm font-medium hover:bg-sidebar-primary/90 transition-colors flex items-center gap-2">
          <Video className="w-4 h-4" />
          Generate Clip (Veo3)
        </button>
        <button className="w-full bg-sidebar-accent text-sidebar-accent-foreground px-3 py-2 rounded text-sm font-medium hover:bg-sidebar-accent/90 transition-colors flex items-center gap-2">
          <Zap className="w-4 h-4" />
          Character Animation
        </button>
        <button className="w-full bg-secondary text-secondary-foreground px-3 py-2 rounded text-sm font-medium hover:bg-secondary/90 transition-colors flex items-center gap-2">
          <Palette className="w-4 h-4" />
          Visual Enhancement
        </button>
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-medium text-sidebar-foreground">
          Film Settings
        </h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Style:</span>
            <span className="text-sidebar-foreground">Corporate Modern</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Duration:</span>
            <span className="text-sidebar-foreground">7:30</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Aspect:</span>
            <span className="text-sidebar-foreground">16:9</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Modal Component
const SceneModal = ({
  scene,
  isOpen,
  onClose,
}: {
  scene: any;
  isOpen: any;
  onClose: any;
}) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-popover border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-popover-foreground">
                {scene?.title}
              </h2>
              <button
                onClick={onClose}
                className="p-1 hover:bg-muted rounded transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="aspect-video bg-muted rounded-lg mb-4 overflow-hidden">
              <img
                src={scene?.thumbnail || "/placeholder.svg"}
                alt={scene?.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-popover-foreground mb-2">
                  Scene Description
                </h3>
                <p className="text-muted-foreground">{scene?.description}</p>
              </div>

              <div>
                <h3 className="font-medium text-popover-foreground mb-2">
                  Characters
                </h3>
                <div className="flex flex-wrap gap-2">
                  {scene?.characters.map((character: any, index: any) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                    >
                      {character}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-popover-foreground mb-2">
                  Duration
                </h3>
                <p className="text-muted-foreground flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {scene?.duration}
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded font-medium hover:bg-primary/90 transition-colors">
                  Generate Scene
                </button>
                <button className="px-4 py-2 bg-secondary text-secondary-foreground rounded font-medium hover:bg-secondary/90 transition-colors">
                  Edit Scene
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// Main Film Director Studio Component
export default function FilmDirectorPage() {
  const [selectedScene, setSelectedScene] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSceneClick = (scene: any) => {
    setSelectedScene(scene);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedScene(null);
  };

  return (
    <div className="min-h-screen bg-background dark">
      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-card border-b border-border px-6 py-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Film className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-card-foreground">
              Film Director Studio
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2">
              <Play className="w-4 h-4" />
              Preview Film
            </button>
          </div>
        </div>
      </motion.header>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-6xl mx-auto space-y-8">
            {/* Characters Section */}
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Characters
                </h2>
                <button className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add Character
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sampleCharacters.map((character, index) => (
                  <motion.div
                    key={character.id}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1 + index * 0.1 }}
                  >
                    <CharacterCard
                      character={character}
                      onClick={() =>
                        console.log("Character clicked:", character.name)
                      }
                    />
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* Scenes Section */}
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Scenes
                </h2>
                <button className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add Scene
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sampleScenes.map((scene, index) => (
                  <motion.div
                    key={scene.id}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                  >
                    <SceneCard scene={scene} onClick={handleSceneClick} />
                  </motion.div>
                ))}
              </div>
            </motion.section>

            {/* Timeline Section */}
            <motion.section
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Timeline scenes={sampleScenes} />
            </motion.section>
          </div>
        </div>

        {/* Tools Sidebar */}
        <motion.aside
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="w-80 flex-shrink-0"
        >
          <ToolsSidebar />
        </motion.aside>
      </div>

      {/* Scene Modal */}
      <SceneModal
        scene={selectedScene}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}
